import React, { ChangeEvent, CSSProperties, useContext } from "react";
import Input from "common/Inputs/Input";
import Text from "common/Typography/Text";
import AutoSizeInput from "common/Inputs/AutoSizeInput";
import CustomInputNumber from "common/Inputs/CustomInputNumber";
import AntCloseOutlined from "@ant-design/icons/CloseOutlined";
import LineOutlined from "@ant-design/icons/LineOutlined";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import Styles from "features/configuration/GeneralConfiguration/ConfigurationItem/styles";
import Select from "common/Select/Select";
import { typography } from "@shared/common/Themes/typography";
import styled from "styled-components";
import {
  CustomStrategyError,
  useCustomStrategyFormProps,
} from "@shared/features/bots/BotForm/common/types";
import TitleWithToggle from "features/bots/BotForm/common/TitleWithToggle";
import { BotStrategiesContext } from "@shared/features/bots/BotForm/Strategies/BotStrategies.context";
import Link from "common/Typography/Link";
import Col from "antd/es/col";
import Row from "antd/es/row";
import CardSorters from "common/CardSorters/CardSorters";
import Flex from "common/Boxes/Flex";
import RequiredFieldMessage from "common/Animations/RequiredFieldMessage";
import { PERCENT_PRECISION } from "@shared/features/tradingTerminal/common/constants";

const useCustomStrategyForm = ({
  includeNameSelect,
  style,
}: useCustomStrategyFormProps & { style?: CSSProperties }) => {
  const {
    nameInputData,
    customStrategyErrors,
    numTargetsSelectorData,
    targetsData,
  } = useContext(BotStrategiesContext)!;

  const columnSpans = [12, 6, 6];

  const nameAndError = includeNameSelect ? (
    <>
      <Text style={{ marginBottom: 8 }}>Strategy Name</Text>
      <Input
        value={nameInputData.value}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          nameInputData.onChange(e.target.value)
        }
        ref={nameInputData.ref}
      />
      {nameInputData.error && (
        <Text
          type="danger"
          style={{ alignSelf: "flex-end", marginTop: 6, display: "block" }}
        >
          {nameInputData.error}
        </Text>
      )}
    </>
  ) : undefined;

  const targetsHeader = (
    <Styles.NumTargetsRow>
      <Text style={{ margin: 0 }}>
        {targetsData.numTargets} Target{targetsData.numTargets > 1 ? "s" : ""}
      </Text>
      <div>
        <Link
          style={{ marginRight: 12 }}
          onClick={() => targetsData.removeTarget()}
          disabled={targetsData.disabledRemove}
        >
          <LineOutlined />
        </Link>
        <Link
          onClick={() => targetsData.addTarget()}
          disabled={targetsData.disabledAdd}
        >
          <PlusOutlined />
        </Link>
      </div>
    </Styles.NumTargetsRow>
  );

  const targetsColumnsHeader = (
    <CardSorters
      columnNames={["Target Number", "Ratio", ""]}
      columnSpans={columnSpans}
      style={{ margin: "12px 0 16px", padding: "8px 12px" }}
    />
  );

  const getTargetInput = (target: number | string, index: number) => (
    <TargetInputWrapper>
      <AutoSizeInput
        minWidth={15}
        input={
          <CustomInputNumber
            onChange={(percent?: number) => {
              targetsData.updateTarget(index, percent || 0);
            }}
            bordered={false}
            autoSize={true}
            stringMode={true}
            value={target}
            placeholder={"0"}
            precision={PERCENT_PRECISION}
            style={{ paddingLeft: 0, paddingRight: 0 }}
          />
        }
      />
      %
    </TargetInputWrapper>
  );

  const getRow = (
    columns: (React.JSX.Element | undefined)[],
    errorMessage?: string,
    style?: CSSProperties,
    key?: number
  ) => {
    return (
      <div style={style} key={key}>
        <Row>
          <Col
            span={columnSpans[0]}
            style={{ display: "flex", alignItems: "center" }}
          >
            {columns[0]}
          </Col>
          <Col span={columnSpans[1]}>{columns[1]}</Col>
          <Col span={columnSpans[2]} style={{ textAlign: "end" }}>
            {columns[2]}
          </Col>
        </Row>
        <RequiredFieldMessage
          requirement={!!errorMessage}
          errorMessage={errorMessage || ""}
          style={{ paddingTop: 8 }}
        />
      </div>
    );
  };

  const getTargetsComponent = () => {
    return targetsData.parsedTargets?.map((target, index) => {
      const errorMessage =
        customStrategyErrors[CustomStrategyError.TARGET][index];
      const targetNumber = (
        <>
          <NumberCircle>{index + 1}</NumberCircle>
          <Text>{`Target ${index + 1}`}</Text>
        </>
      );
      const removeButton = (
        <CloseOutlined onClick={() => targetsData.removeTarget(index)} />
      );
      const style = { marginBottom: 12 };
      return getRow(
        [targetNumber, getTargetInput(target, index), removeButton],
        errorMessage,
        style,
        index
      );
    });
  };

  const getTotal = () => {
    const errorMessage =
      customStrategyErrors[CustomStrategyError.TOTAL_TARGETS];
    const total = (
      <Text $weight={typography.fontWeights.MEDIUM} style={{ marginLeft: 36 }}>
        Total
      </Text>
    );
    const sum = (
      <Text $weight={typography.fontWeights.MEDIUM}>
        {targetsData.targetsSum} %
      </Text>
    );
    return getRow([total, sum, undefined], errorMessage);
  };

  const getTargetsSelector = () => {
    const content = (
      <div style={{ marginTop: 12, ...style }}>
        <div>{targetsHeader}</div>
        {targetsColumnsHeader}
        {getTargetsComponent()}
        {getTotal()}
      </div>
    );
    if (targetsData.showOverrideButton) {
      return (
        <TitleWithToggle
          content={content}
          getDefaultValue={targetsData.onOverridePress}
          showMultipleValues={true}
          overrideObjectName="Targets"
        />
      );
    }
    return content;
  };

  const numTargetsSelect = numTargetsSelectorData.shouldDisplay ? (
    <Select
      style={{ width: "100%", opacity: numTargetsSelectorData.opacity }}
      value={numTargetsSelectorData.value}
      onChange={numTargetsSelectorData.onChange}
      popupMatchSelectWidth={false}
      optionLabelProp="label"
      options={numTargetsSelectorData.options}
    />
  ) : undefined;

  return {
    nameAndError,
    targetsComponent: getTargetsSelector(),
    numTargetsSelect,
    updatedName: nameInputData.value,
  };
};

export default useCustomStrategyForm;

const TargetInputWrapper = styled(Flex)`
  justify-content: flex-start;
  border-bottom: ${(p) => p.theme.borders.light};
  width: fit-content;
  padding: 0 4px;
  color: ${(p) => p.theme.colors.textColor};
`;
const NumberCircle = styled(Flex)`
  border: ${(p) => p.theme.borders.light};
  border-radius: 50%;
  width: 20px;
  height: 20px;
  color: ${(p) => p.theme.colors.secondaryGrey};
  justify-content: center;
  margin-right: 16px;
`;
const CloseOutlined = styled(AntCloseOutlined)`
  font-size: 13px;
  color: ${(p) => p.theme.colors.textColor};
`;
